/**
 * Retour de validation — haptique si la plateforme en fournit, visuel toujours
 * (specs §8.1 : « retour haptique / animations courtes sur les actions de
 * validation »).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🔴 ÉTAT RÉEL DE L'API VIBRATION SUR SAFARI iOS — vérifié, pas supposé
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Vérifié en août 2026, parce que la documentation courante se contredit :
 *
 *  1. **`navigator.vibrate` n'a jamais été implémenté dans WebKit.** Il vaut
 *     `undefined` sur Safari iOS, **y compris en PWA installée sur l'écran
 *     d'accueil** — l'installation ne change rien, contrairement au Web Push.
 *  2. Les rapports récents affirmant « ça marche maintenant sur iOS » (dont un
 *     ticket ouvert chez MDN) sont **de faux positifs** : ils viennent de sites
 *     qui embarquent un *polyfill* remplaçant `navigator.vibrate`. C'est le
 *     polyfill qui répond, pas le moteur.
 *  3. Ce polyfill exploite le seul vrai chemin haptique de WebKit : le contrôle
 *     `<input type="checkbox" switch>` (Safari 17.4+), dont **la bascule joue un
 *     retour tactile système**. Il fabriquait ce retour en appelant `click()`
 *     par script sur un interrupteur caché.
 *  4. 🔴 **Apple a fermé ce contournement dans iOS 26.5** : seul un appui réel
 *     sur un vrai interrupteur déclenche encore le retour. Aucun appel par
 *     script ne le produit plus.
 *
 * Conséquences retenues pour cette application :
 *
 *  - **Aucun contournement.** Déguiser un bouton de validation en interrupteur
 *    caché pour lui voler un retour tactile coûterait la sémantique du contrôle
 *    le plus utilisé de l'app (VoiceOver annoncerait « interrupteur » sur
 *    « Ajouter au journal ») pour un comportement qu'Apple vient de fermer une
 *    version mineure plus tôt. C'est exactement le genre de dépendance qui
 *    meurt en silence.
 *  - **Le retour de validation est donc visuel** — `.is-confirming` dans
 *    `surfaces.css` — et c'est lui qui part à coup sûr sur l'appareil cible.
 *  - **La vibration est tentée quand même**, sous détection : là où la
 *    plateforme la fournit (Android, Chrome), elle s'ajoute gratuitement. Sur
 *    iOS elle ne part pas, et rien ne casse.
 *  - **Là où un interrupteur est réellement un interrupteur** — les trois
 *    réglages de notification — on emploie `<input type="checkbox" switch>` :
 *    l'iPhone y donne son retour tactile natif, sans une ligne de JavaScript.
 *    C'est le seul haptique que l'application obtienne sur la plateforme cible,
 *    et il est obtenu en utilisant le contrôle pour ce qu'il est.
 */

/**
 * La plateforme expose-t-elle l'API Vibration ?
 *
 * ⚠️ Testé, jamais appelé « pour voir » : sur un moteur qui ne l'implémente pas,
 * `navigator.vibrate(...)` lève un `TypeError` — un appel non gardé casserait la
 * validation au lieu de ne rien faire.
 */
export function vibrationSupported(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';
}

/**
 * Petit coup sec de validation. **Ne remplace jamais le retour visuel** : sur
 * l'appareil cible, cette fonction ne fait rien du tout.
 *
 * Retourne ce qui s'est réellement produit, pour que le banc puisse le vérifier
 * plutôt que de le supposer.
 */
export function confirmTap(): 'vibrated' | 'unsupported' | 'refused' {
  if (!vibrationSupported()) return 'unsupported';
  try {
    // 10 ms : un « tic », pas une sonnerie. Au-delà de ~20 ms le retour se lit
    // comme une alerte et devient désagréable à chaque saisie d'aliment.
    return navigator.vibrate(10) ? 'vibrated' : 'refused';
  } catch {
    return 'refused';
  }
}
